import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { useAuthentication } from '@/hooks/useAuthentication';
import { ReservoirAction } from '@/utils/reservoir';
import { Button, ButtonProps, Tooltip, useDisclosure } from '@chakra-ui/react';
import { CartIcon, MinusIcon, PlusIcon } from '@sphere/icons';
import { DynamicTokens, useCart } from '@sphere/reservoir-kit-ui';
import { QuantityPicker, QuantityPickerProps } from '@sphere/ui';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { toast } from 'sonner';
import { ConfirmationModal } from '../modals/ConfirmationModal';
import ActionDisabledTooltip from './ActionDisabledTooltip';

type Props = ButtonProps & {
  token?: DynamicTokens[number];
  symbol?: string;
  orderId?: string;
  onOpen?: () => void;
  onClose?: () => void;
  quantityPickerProps?: QuantityPickerProps;
};

export const AddToCart = ({
  token,
  orderId: specificOrderId,
  symbol: specificCurrencySymbol,
  onOpen,
  onClose,
  quantityPickerProps,
  ...buttonProps
}: Props) => {
  const { t } = useTranslation('common');
  const { data: items, add, remove, clear, setQuantity } = useCart(cart => cart.items);
  const { data: cartChain } = useCart(cart => cart.chain);
  const { isAuthenticated, authenticate } = useAuthentication();
  const marketplaceChain = useMarketplaceChain();
  const {
    isOpen: isConfirmationModalOpen,
    onOpen: onOpenConfirmationModal,
    onClose: onCloseConfirmationModal,
  } = useDisclosure();

  const orderId = specificOrderId || (token && 'market' in token && token.market?.floorAsk?.id);

  if (!orderId) return null;

  const hasOtherCurrencyInCart = items.some(
    item =>
      ![token?.market?.floorAsk?.price?.currency?.symbol, specificCurrencySymbol].includes(
        item.price?.currency?.symbol,
      ),
  );

  const cartOrderItem = items.find(item => item.order?.id === orderId)?.order;
  const shouldShowQuantityPicker =
    isAuthenticated && !!cartOrderItem && (cartOrderItem?.quantityRemaining || 0) > 1;

  const handleAddToCart = () => {
    if (!isAuthenticated) return authenticate();

    if (hasOtherCurrencyInCart && !marketplaceChain.multiCurrencyCartEnabled) return;

    if (cartOrderItem) {
      remove([orderId]);
    } else if (cartChain && cartChain?.id !== marketplaceChain.id) {
      onOpenConfirmationModal();
      onOpen?.();
    } else {
      add([{ orderId: orderId }], marketplaceChain.id).then(() => {
        toast.success(t('cart.toasts.nft-added.label', { count: 1 }));
      });
    }
  };

  return (
    <>
      <ActionDisabledTooltip action={ReservoirAction.Buy}>
        {shouldShowQuantityPicker ? (
          <QuantityPicker
            onChange={newQuantity => {
              if (cartOrderItem?.quantity === newQuantity) return;
              setQuantity(orderId, newQuantity);
            }}
            onRemove={() => remove([orderId])}
            max={cartOrderItem?.quantityRemaining}
            value={cartOrderItem?.quantity}
            size={rem(36)}
            iconSize={rem(12)}
            textStyles={{ fontSize: 'sm' }}
            rounded="full"
            tooltipText={t('cart.quantity-picker.tooltip-label', {
              count: cartOrderItem?.quantityRemaining,
            })}
            {...quantityPickerProps}
          />
        ) : (
          <Tooltip
            isDisabled={!hasOtherCurrencyInCart || marketplaceChain.multiCurrencyCartEnabled}
            label={t('cart.errors.currency-mismatch', {
              cartSymbol: items[0]?.price?.currency?.symbol,
              itemSymbol:
                specificCurrencySymbol || token?.market?.floorAsk?.price?.currency?.symbol,
              count: items.length,
            })}
            placement="top"
            hasArrow
            arrowSize={8}
          >
            <Button
              onClick={handleAddToCart}
              px="space.16"
              iconSpacing="space.2"
              leftIcon={
                cartOrderItem ? <MinusIcon boxSize={rem(14)} /> : <PlusIcon boxSize={rem(14)} />
              }
              rightIcon={<CartIcon boxSize={rem(20)} />}
              variant="cta-secondary"
              isDisabled={hasOtherCurrencyInCart && !marketplaceChain.multiCurrencyCartEnabled}
              {...buttonProps}
            />
          </Tooltip>
        )}
      </ActionDisabledTooltip>

      <ConfirmationModal
        title={t('cart.toasts.nft-added.error', { count: 1 })}
        message={t('cart.errors.network-lock')}
        isOpen={isConfirmationModalOpen}
        onClose={() => [onCloseConfirmationModal(), onClose?.()]}
        onConfirmed={confirmed => {
          if (confirmed) {
            clear();
            add([{ orderId: orderId }], marketplaceChain.id);
          }
        }}
      />
    </>
  );
};
